import {
  ApplicationTypeEnumType,
  FederalGroupEnumType,
} from "@libs/api/patients";
import { i18n } from "@/i18n";

// Ordered youngest-first for brand composition
const FEDERAL_GROUP_AGE_ORDER: FederalGroupEnumType[] = [
  "CHILD_0_1",
  "CHILD_1_5",
  "CHILD_6_18",
  "CHILD_1_18",
  "CHILD_0_18_CHIP",
  "ADULT_19_20",
  "FORMERLY_FOSTER_CARE",
  "PREGNANT_WOMEN",
  "PARENT_CARETAKER",
  "EXPANSION_ADULTS_19_64",
  "BREAST_CANCER",
  "CERVICAL_CANCER",
  "NO_EMPLOYER_COVERAGE",
  "AGE_SINGLE",
  "BLIND_SINGLE",
  "DISABLED_SINGLE",
  "AGE_MARRIED",
  "BLIND_MARRIED",
  "DISABLED_MARRIED",
  "AGE",
  "DISABLED",
];

const GENERIC_NAMES = ["MEDICAID", "MARKETPLACE"];

// Precomputed lookup for O(1) index access in the sort comparator.
// Unknown groups fall to the end (index = array length).
const FEDERAL_GROUP_INDEX_MAP = new Map(
  FEDERAL_GROUP_AGE_ORDER.map((group, i) => [group, i]),
);
const UNKNOWN_GROUP_INDEX = FEDERAL_GROUP_AGE_ORDER.length;

const sortByAgeOrder = <T extends { federalGroup: FederalGroupEnumType }>(
  items: T[],
): T[] =>
  [...items].sort(
    (a, b) =>
      (FEDERAL_GROUP_INDEX_MAP.get(a.federalGroup) ?? UNKNOWN_GROUP_INDEX) -
      (FEDERAL_GROUP_INDEX_MAP.get(b.federalGroup) ?? UNKNOWN_GROUP_INDEX),
  );

export const getProgramTypeLabel = (type: ApplicationTypeEnumType): string => {
  const labels: Record<ApplicationTypeEnumType, string> = {
    MEDICAID: i18n.t("common:medicaid"),
    MARKETPLACE: i18n.t("common:marketplace"),
    MEDICARE: i18n.t("common:medicare"),
  };
  return labels[type];
};

type BrandInputType = {
  customUsStateName: string | null;
  federalGroup: FederalGroupEnumType;
};

/**
 * Composes a brand label from brand objects, sorted youngest-first.
 *
 * Rules:
 * - Sorts by FEDERAL_GROUP_AGE_ORDER (youngest-first)
 * - Deduplicates and trims brand names
 * - Excludes names that case-insensitively equal "Medicaid" or "Marketplace"
 * - Empty list → plain program type ("Medicaid" / "Marketplace")
 * - Composed string contains "Medicaid"/"Marketplace" → no parenthesised suffix
 * - Otherwise → appends " (Medicaid)" or " (Marketplace)"
 * - Medicare always returns "Medicare"
 */
export const composeBrandLabel = (
  brands: BrandInputType[],
  applicationType: ApplicationTypeEnumType,
): string => {
  if (applicationType === "MEDICARE") return getProgramTypeLabel("MEDICARE");

  const programLabel = getProgramTypeLabel(applicationType);

  const sorted = sortByAgeOrder(brands);

  const filtered = [
    ...new Set(
      sorted
        .map(b => b.customUsStateName?.trim())
        .filter(
          (b): b is string =>
            Boolean(b) && !GENERIC_NAMES.includes(b!.toUpperCase()),
        ),
    ),
  ];

  if (filtered.length === 0) return programLabel;

  const composed = filtered.join(", ");

  if (
    composed.toLowerCase().includes("medicaid") ||
    composed.toLowerCase().includes("marketplace")
  ) {
    return composed;
  }

  return `${composed} (${programLabel})`;
};

type EligibilityResultWithMemberType = BrandInputType & {
  householdMember: { firstName: string | null };
};

/**
 * Groups eligibility results by brand name, ordered youngest-first.
 * Returns brand → member first names mapping for per-brand eligibility display.
 */
export const groupResultsByBrand = (
  results: EligibilityResultWithMemberType[],
  applicationType: ApplicationTypeEnumType,
): { brand: string; memberNames: string[] }[] => {
  const programLabel = getProgramTypeLabel(applicationType);

  const sorted = sortByAgeOrder(results);

  // Group by effective brand name (after filtering generics)
  const brandGroups = new Map<string, Set<string>>();

  for (const result of sorted) {
    const rawBrand = result.customUsStateName?.trim();
    const isGeneric =
      !rawBrand || GENERIC_NAMES.includes(rawBrand.toUpperCase());
    const effectiveBrand = isGeneric ? programLabel : rawBrand;
    const memberName = result.householdMember.firstName ?? "";

    if (!memberName) continue;

    const existing = brandGroups.get(effectiveBrand) ?? new Set<string>();
    existing.add(memberName);
    brandGroups.set(effectiveBrand, existing);
  }

  return Array.from(brandGroups.entries()).map(([brand, names]) => ({
    brand,
    memberNames: Array.from(names),
  }));
};

/**
 * Formats a list of member names as "A, B and C".
 * Accepts a translated connector word (e.g. "and", "y", "и").
 */
export const formatMemberList = ({
  names,
  connector = i18n.t("common:listConnector"),
  separator = i18n.t("common:listSeparator"),
}: {
  names: string[];
  connector?: string;
  separator?: string;
}): string => {
  if (names.length === 0) return "";
  if (names.length === 1) return names[0]!;
  return `${names.slice(0, -1).join(separator)}${connector}${names[names.length - 1]}`;
};
